import { useState } from 'react';

import { Button } from '../../components/ui/Button';
import { ApiError, api } from '../../lib/api';
import { useCan } from '../../stores/session';

/**
 * The original file, for a moderator who has to do something with it that
 * a browser cannot: a fingerprint against a rights database, a listen in
 * an editor, a copy attached to a DMCA answer.
 *
 * Nothing for anybody without `track.download.any`, which nobody holds by
 * default except an administrator. Every use is written to the audit log
 * before the link exists - see the backend's `DownloadTrackAction` - so
 * this says so out loud rather than letting somebody find out later.
 *
 * The link is presigned and short lived, and the browser is sent straight
 * at it: the file comes from the bucket, not through the application.
 */
export function DownloadOriginal({ trackId, owned }: { trackId: number; owned: boolean }) {
  const allowed = useCan('track.download.any');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  // An artist downloading their own upload is a feature nobody asked for;
  // this is the moderation one, and the note about the log would read as a
  // threat on your own track.
  if (!allowed || owned) return null;

  async function download() {
    setBusy(true);
    setProblem(null);

    try {
      const { url } = await api.get<{ url: string }>(`/admin/tracks/${trackId}/download`);

      // `location.assign` rather than a new tab: the response is an
      // attachment, so nothing navigates away - the browser saves it and
      // this page stays where it is. A popup blocker never sees it.
      window.location.assign(url);
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : 'Could not get the file.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-1" data-download-original>
      <Button variant="ghost" loading={busy} onClick={() => void download()}>
        Download original
      </Button>
      <p className="meta text-ghost">The audit log records who downloads a track, and when.</p>
      {problem !== null && <p className="text-[13px] text-negative">{problem}</p>}
    </div>
  );
}
